import Image from 'next/image';
import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { WORKSHOP_CONTACT_ID } from '@/utils/scrollToSection';

import { useB2bTranslation } from './kit';

// Photos from our own workshops.
const PHOTOS = {
  handsOn: '/images/about-us/Workshop1-1-1.jpg',
  class: '/images/about-us/easy-peasy.ai-workshop-march25.jpg',
  group: '/images/about-us/huttons_workshop.JPG',
};

export default function TeamsTraining() {
  const { t } = useB2bTranslation();
  const { ref, state } = useReveal<HTMLDivElement>(0.2);

  const points = [
    t('A private session, just for your team'),
    t('Built around the work your team actually does'),
    t('Run by the people who build Easy-Peasy.AI'),
  ];

  return (
    <section id='training' className='py-20 sm:py-28'>
      <div className='mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:items-center lg:gap-16 lg:px-8'>
        <div className='lg:col-span-5'>
          <SectionHeading
            size='md'
            title={t('We’ll get your team started.')}
            tail={t('With a practical workshop.')}
            lead={t(
              'We run practical AI workshops for companies, built around your goals. Everyone leaves knowing how to use AI in their own work.',
            )}
          />
          <ul className='mt-8 space-y-3.5'>
            {points.map((point) => (
              <li
                key={point}
                className={clsxm('flex gap-3 text-[16px] leading-7', tone.body)}
              >
                <span className='mt-1.5 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-primary-600 text-white'>
                  <CheckGlyph className='h-2.5 w-2.5' />
                </span>
                {point}
              </li>
            ))}
          </ul>
          <Link
            href={`/ai-workshops#${WORKSHOP_CONTACT_ID}`}
            className={clsxm(buttonPrimary, 'mt-9')}
          >
            {t('Plan a workshop')}
            <ArrowGlyph />
          </Link>
        </div>

        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(
            styles.reveal,
            'grid grid-cols-6 grid-rows-[auto_auto] gap-3 sm:gap-4 lg:col-span-7',
          )}
        >
          <figure
            className={clsxm(
              styles.rv,
              'relative col-span-6 aspect-[16/10] overflow-hidden rounded-[28px] bg-[#e8e8f0] sm:col-span-4 sm:row-span-2 sm:aspect-auto sm:min-h-[26rem] dark:bg-white/5',
            )}
          >
            <Image
              src={PHOTOS.handsOn}
              alt={t('A hands-on AI workshop with a team of business owners')}
              fill
              sizes='(min-width: 1024px) 480px, (min-width: 640px) 66vw, 100vw'
              className='object-cover object-[60%_40%]'
            />
          </figure>
          <figure
            className={clsxm(
              styles.rv,
              'relative col-span-3 aspect-[4/3] overflow-hidden rounded-[24px] bg-[#e8e8f0] sm:col-span-2 sm:aspect-auto dark:bg-white/5',
            )}
            style={delay(120)}
          >
            <Image
              src={PHOTOS.class}
              alt={t('An Easy-Peasy.AI trainer teaching a class')}
              fill
              sizes='(min-width: 1024px) 240px, (min-width: 640px) 33vw, 50vw'
              className='object-cover'
            />
          </figure>
          <figure
            className={clsxm(
              styles.rv,
              'relative col-span-3 aspect-[4/3] overflow-hidden rounded-[24px] bg-[#e8e8f0] sm:col-span-2 sm:aspect-auto dark:bg-white/5',
            )}
            style={delay(240)}
          >
            <Image
              src={PHOTOS.group}
              alt={t('A company team after their AI workshop')}
              fill
              sizes='(min-width: 1024px) 240px, (min-width: 640px) 33vw, 50vw'
              className='object-cover'
            />
          </figure>
        </div>
      </div>
    </section>
  );
}
